import type { Mission, MissionStep, Npc } from '@/content/schema';
import type { CardChoice, CardOpts, Ctx } from '@/core/context';
import { bus } from '@/core/bus';
import { nextRandom, shuffle } from '@/core/rng';
import { bump } from './achievements';
import { whoSees } from './watchers';
import { grantItem } from './items';
import { basketHeldOrOnCart, consumeBasket } from './carry';
import { gainPoints, gainXp } from './progress';
import { tr } from '@/i18n';
import { basketLabel } from './flow';
import type { MiniResult } from '@/minigames/types';
import { missingNeeds, needLines, stepNeeds, tempSpots } from './worksites';

/**
 * 任務系統（v0.4／v0.5）：content/missions/ 裡的每個檔案就是一個任務，遊戲自動讀取。
 *   daily 每日任務（v0.5）：職務的一天日常（報到 → 看交接 → 上午工作 → 吃飯 → 下午工作 → 交接），第 1～3 天各一份
 *   main 主線：職務每天的工作（roles.json 的 missions）；第 1 天只排 1 個、第 2 天 2 個、第 3 天起全部
 *   ※ 不是自己職務的任務（別的職務的工作、大家都能接的任務），遊戲裡一律叫「支線任務」
 *   side 支線：和人聊天、走到某處、某個旗標成立時開始；once=true 一輩子做一次
 *   special 特殊：通常由別的事件觸發（例如零數包沒標記），可以有 timeout
 *   accident 單一工傷事故／短事件：走的是 events.ts 的事件流程
 * 每一步：到哪裡（floor + target）、做什麼（do）、一句話說明（say）、卡住時的提示（hint）。
 * 設計原則：一步只做一件事、最多二～三選一、不要多層分支——幼稚園小朋友也看得懂。
 */

type Day = Ctx['day'];
const isFor = (ctx: Ctx, m: Mission) => {
  const role = ctx.c.roleById.get(ctx.day.roleId)!;
  const f = m.for ?? {};
  if (!f.roles && !f.depts) return true;
  return !!(f.roles?.includes(role.id) || f.depts?.includes(role.dept));
};
const available = (ctx: Ctx, m: Mission) => !(m.once && ctx.profile.missionsDone.includes(m.id)) && !(m.id in ctx.day.missions) && isFor(ctx, m);

export type MissionKind = 'daily' | 'main' | 'side' | 'special';
export const KIND_NAME: Record<MissionKind, string> = { daily: tr("每日任務"), main: tr("主線任務"), side: tr("支線任務"), special: tr("特殊任務") };
export const KIND_COLOR: Record<MissionKind, string> = { daily: '#f2c84b', main: '#7fbf98', side: '#6fa8c9', special: '#e07b39' };
/** 這個任務對「現在這個職務」來說是什麼：自己職務的日常＝每日、自己職務的工作＝主線，其他（別的職務、大家都能接的）＝支線 */
export function missionKind(ctx: Ctx, m: Mission): MissionKind {
  if (m.type === 'special') return 'special';
  const role = ctx.c.roleById.get(ctx.day.roleId);
  const mine = !!role && (role.missions.includes(m.id) || !!m.for?.roles?.includes(role.id) || !!m.for?.depts?.includes(role.dept));
  if (m.type === 'daily') return mine ? 'daily' : 'side';
  if (m.type === 'main') return mine ? 'main' : 'side';
  return 'side';
}

/** 這個職務做到第幾天（同一天重開不重算）→ 每日任務用第幾份（1、2、3；第 4 天起輪流 2、3） */
export function roleDayIndex(ctx: Ctx): { n: number; idx: number } {
  const role = ctx.day.roleId; const p = ctx.profile;
  const k = `roleDays:${role}`, last = `roleDayLast:${role}`;
  if (p.counters[last] !== p.day) { p.counters[k] = (p.counters[k] ?? 0) + 1; p.counters[last] = p.day; }
  const n = p.counters[k] ?? 1;
  return { n, idx: n <= 3 ? n : n % 2 === 0 ? 2 : 3 };
}

export const isMinigameMission = (m: Mission) => m.steps.some(s => s.do === 'minigame');
export function planMissions(ctx: Ctx): void {
  const role = ctx.c.roleById.get(ctx.day.roleId)!;
  ctx.day.missionQueue = []; ctx.day.missions = {}; ctx.day.missionStartedAt = {};
  const { idx } = roleDayIndex(ctx);
  ctx.day.flags[`dailyDay${idx}`] = true;
  if (ctx.day.tutorial) { const t = ctx.c.missionById.get('M-tutorial'); if (t) addMission(ctx.day, t.id); }
  // v1.0 自由模式（實習生）：沒有每日、主線，只有大家都能接的支線
  if (ctx.day.mode !== 'intern') {
    for (const m of ctx.c.missions) if (m.type === 'daily' && m.for?.roles?.includes(role.id) && m.days?.includes(idx)) addMission(ctx.day, m.id);
    for (const id of role.missions.slice(0, idx >= 3 ? undefined : idx)) addMission(ctx.day, id);
    // v1.0 這個職務的作業小遊戲：每天隨機抽一個當主線
    const mg = ctx.c.missions.filter(m => isMinigameMission(m) && m.for?.roles?.includes(role.id) && !(m.id in ctx.day.missions));
    if (mg.length) addMission(ctx.day, mg[Math.floor(nextRandom(ctx.day) * mg.length)].id);
  }
  for (const m of ctx.c.missions) if ((m.type === 'side' || m.type === 'special') && m.start.when === 'day' && available(ctx, m)) addMission(ctx.day, m.id);
}
/** v1.0 接下別的職務的工作（作業小遊戲任務）＝支線 */
export function takeJob(ctx: Ctx, m: Mission, at?: string): boolean {
  if (m.id in ctx.day.missions) return false;
  addMission(ctx.day, m.id);
  ctx.day.focus = m.id;
  bus.emit('sfx', 'item');
  ctx.save();
  if (!startHere(ctx, m, at)) jobBrief(ctx, m);
  return true;
}
/** v1.1.1 按「是」之後：如果這一步就在這台機器，直接開始作業（不用再點一次機器） */
function startHere(ctx: Ctx, m: Mission, at?: string): boolean {
  const st = missionStep(ctx, m.id);
  if (!at || !st || st.target !== at || st.floor !== ctx.day.floor || st.do !== 'minigame') return false;
  return runMission(ctx, m);
}
/** v1.1 失敗的任務可以再挑戰一次（每個任務一天限一次）：從失敗的那一步重來 */
export function canRetry(ctx: Ctx, id: string): boolean {
  const pr = ctx.day.missions[id]; return !!pr?.failed && !(ctx.day.retried ?? []).includes(id);
}
export function retryMission(ctx: Ctx, id: string, at?: string): boolean {
  if (!canRetry(ctx, id)) return false;
  const pr = ctx.day.missions[id]; pr.done = false; pr.failed = false;
  (ctx.day.retried ??= []).push(id); ctx.day.focus = id;
  bump(ctx, 'retried');
  const m = ctx.c.missionById.get(id)!;
  ctx.log(tr("再挑戰一次：{0}", m.title), 'info');
  ctx.world.refreshActors(); ctx.save();
  if (!startHere(ctx, m, at)) jobBrief(ctx, m);
  return true;
}
/** v1.1 接下工作後的大提示：下一步去哪裡、拿什麼（場景裡也會出現黃色「!」） */
export function jobBrief(ctx: Ctx, m: Mission): void {
  const st = missionStep(ctx, m.id); if (!st) return;
  const kind = missionKind(ctx, m);
  const items: string[] = [];
  if (st.do === 'carry' && st.carry) {
    const src = carrySources(ctx, st.carry.content);
    const name = basketLabel(ctx.c, st.carry.content);
    items.push(tr("① 先去拿一籃「{0}」：{1}", name, src.length ? [...new Set(src.map(p => `${p.floor}・${p.label}`))].slice(0, 3).join(tr("、")) : tr("問問倉儲或看貨架")));
    items.push(tr("② 拿到後送到：{0}・{1}", st.floor, targetName(ctx, st)));
  } else if (missingNeeds(ctx, st).length) {
    items.push(tr("① 先拿齊這些東西（貨架上點一下就放進背包）："), ...needLines(ctx, st));
    items.push(tr("② 再到：{0}・{1}", st.floor, targetName(ctx, st)));
  } else items.push(tr("到 {0}・{1}：{2}", st.floor, targetName(ctx, st), st.say));
  ctx.ui.card({ name: `${m.icon} ${m.title}`, role: tr("{0}・已接下", KIND_NAME[kind]), color: KIND_COLOR[kind], badge: { text: tr("🎯 接下來"), color: '#f2c84b' },
    title: tr("接下來要做什麼"), text: m.summary,
    sections: [{ label: tr("下一步"), items, tone: 'info' }], more: tr("跟著黃色「!」走；地上的黃色小點會帶路。任務欄點一下任務，可以切換要跟著哪個任務走。"),
    choices: [{ text: tr("出發！"), tone: 'primary', fn: () => ctx.ui.close() }] });
}
/** v1.1 哪裡拿得到這種料：貨架（冰箱）＋地上／車上的籃子 */
export function carrySources(ctx: Ctx, content: string): { floor: string; x: number; z: number; y: number; label: string }[] {
  const out: { floor: string; x: number; z: number; y: number; label: string }[] = [];
  for (const f of ctx.c.floors) for (const e of f.equipment) if (e.stock?.includes(content)) out.push({ floor: f.id, x: e.pos[0], z: e.pos[1], y: e.size[1] + 0.9, label: e.name.split(tr("（"))[0].trim() });
  for (const [id, b] of Object.entries(ctx.day.baskets)) {
    if (b.content !== content || b.on === 'hand') continue;
    if (ctx.day.holding?.id === id) continue;
    const cart = b.on && !b.on.startsWith('bk:') ? ctx.day.carts[b.on] : null;
    const [x, z] = cart ? cart.pos : b.pos;
    out.push({ floor: b.floor, x, z, y: 1.6, label: tr("一籃{0}", basketLabel(ctx.c, content)) });
  }
  return out;
}
function addMission(day: Day, id: string) {
  if (id in day.missions) return;
  day.missionQueue.push(id);
  day.missions[id] = { step: 0, done: false, good: 0, bad: 0 };
  day.missionStartedAt[id] = day.clock;
}

/** 讓支線／特殊任務開始（說話、旗標、走到某處） */
export function startMission(ctx: Ctx, m: Mission, quiet = false): void {
  if (!available(ctx, m)) return;
  addMission(ctx.day, m.id);
  if (!quiet) {
    bus.emit('sfx', 'item');
    ctx.ui.toast(tr("{0} {1}：{2}——{3}", m.icon, KIND_NAME[missionKind(ctx, m)], m.title, m.summary));
  }
  ctx.save();
}

export const missionStep = (ctx: Ctx, id: string): MissionStep | null => {
  const m = ctx.c.missionById.get(id); const pr = ctx.day.missions[id];
  return m && pr && !pr.done ? m.steps[pr.step] ?? null : null;
};

/** 這個目標（設備 id、npc:id）在這層樓有沒有任務等著做 */
export function missionsFor(ctx: Ctx, target: string): Mission[] {
  return ctx.day.missionQueue.map(id => ctx.c.missionById.get(id)!).filter(m => {
    const st = missionStep(ctx, m.id); return !!st && st.floor === ctx.day.floor && st.target === target && st.do !== 'go';
  });
}

/** 現在最該做的任務（HUD「下一步」用）：還沒完成的第一個 */
export function currentMission(ctx: Ctx): { m: Mission; st: MissionStep; index: number } | null {
  const f = ctx.day.focus;
  if (f && ctx.day.missions[f] && !ctx.day.missions[f].done) { const st = missionStep(ctx, f); if (st) return { m: ctx.c.missionById.get(f)!, st, index: ctx.day.missions[f].step }; }
  for (const id of ctx.day.missionQueue) {
    const st = missionStep(ctx, id); if (!st) continue;
    return { m: ctx.c.missionById.get(id)!, st, index: ctx.day.missions[id].step };
  }
  return null;
}

export function targetName(ctx: Ctx, st: MissionStep): string {
  if (st.target.startsWith('npc:')) return ctx.c.npcById.get(st.target.slice(4))?.name ?? st.target;
  if (st.target.startsWith('zone:')) return ctx.c.floorById.get(st.floor)!.zones.find(z => z.id === st.target.slice(5))?.name ?? st.target;
  const f = ctx.c.floorById.get(st.floor)!;
  return f.equipment.find(e => e.id === st.target)?.name.split(tr("（"))[0].trim() ?? f.doors.find(d => d.id === st.target)?.name ?? st.target;
}

/** 💡 提示：沒寫 hint 就自動產生「到 X 樓的 Y 按 E」 */
export function missionHint(ctx: Ctx, st: MissionStep): string {
  const fl = ctx.c.floorById.get(st.floor)!;
  const where = st.floor === ctx.day.floor ? '' : tr("先走樓梯到 {0}（{1}），", st.floor, fl.name);
  const auto = tr("{0}找到「{1}」，靠近後按 E（手機點一下）。地上的黃色箭頭會帶路。", where, targetName(ctx, st));
  const extra = [st.do === 'carry' && st.carry ? tr("要拿著一籃「{0}」。", basketLabel(ctx.c, st.carry.content)) : '',
    st.need && !st.needs ? tr("🎒 背包裡要有「{0}」{1}。", ctx.c.packById.get(st.need)?.name ?? st.need, ctx.day.pack.includes(st.need) ? tr("（有了）") : tr("（還沒拿到：先做前面的步驟）")) : '',
    st.needs?.length ? tr("🎒 要先拿：{0}", needLines(ctx, st).join(tr("；"))) : ''].filter(Boolean).join(' ');
  return [st.hint, extra, st.hint ? where && tr("（{0}）", where.replace(/，$/, '')) : auto].filter(Boolean).join(' ');
}

export function nextMissionText(ctx: Ctx): string | null {
  const cur = currentMission(ctx); if (!cur) return null;
  return tr("{0}【{1}】{2}：{3}", cur.m.icon, cur.m.title, cur.st.floor, cur.st.say);
}

// ------------------------------------------------------------------ 完成
const finishStep = (ctx: Ctx, m: Mission, good?: boolean) => {
  const pr = ctx.day.missions[m.id];
  if (good === true) pr.good++; else if (good === false) pr.bad++;
  const done = m.steps[pr.step];
  if (done) for (const id of stepNeeds(done)) { const i = ctx.day.pack.indexOf(id); if (i >= 0) ctx.day.pack.splice(i, 1); }
  if (done?.give && !ctx.day.pack.includes(done.give)) {
    ctx.day.pack.push(done.give);
    const it = ctx.c.packById.get(done.give);
    setTimeout(() => ctx.ui.toast(tr("🎒 放進背包：{0} {1}", it?.icon ?? '', it?.name ?? done.give)), 1800);
  }
  pr.step++;
  gainXp(ctx, 'step');
  if (pr.step >= m.steps.length) completeMission(ctx, m);
  else {
    const st = m.steps[pr.step];
    ctx.ui.toast(tr("👉 下一步：{0}{1}{2}", st.floor === ctx.day.floor ? '' : tr("{0}・", st.floor), st.say.slice(0, 30), st.say.length > 30 ? '…' : ''));
  }
  ctx.world.refreshActors(); ctx.save();
};

function completeMission(ctx: Ctx, m: Mission): void {
  const pr = ctx.day.missions[m.id];
  pr.done = true;
  const kind = missionKind(ctx, m);
  bump(ctx, kind === 'main' || kind === 'daily' ? 'dutiesDone' : 'sideDone');
  if (kind === 'daily') { bump(ctx, 'dailyDone'); gainPoints(ctx, 'daily', tr("每日任務：{0}", m.title)); }
  gainXp(ctx, kind === 'daily' ? 'daily' : 'mission', m.title);
  if (m.once && !ctx.profile.missionsDone.includes(m.id)) ctx.profile.missionsDone.push(m.id);
  const r = m.reward;
  if (r?.effects) ctx.apply(r.effects, m.id);
  if (r?.flag) ctx.profile.flags[r.flag] = true;
  if (r?.item) grantItem(ctx, r.item, true);
  ctx.log(tr("任務完成：{0}{1}", m.title, pr.bad ? tr("（{0} 個判斷可以更好）", pr.bad) : ''), pr.bad ? 'warn' : 'good');
  ctx.ui.toast(tr("✅ 完成：{0} {1}{2}", m.icon, m.title, r?.text ? `——${r.text}` : ''));
  bus.emit('sfx', 'ok');
  const mains = ctx.day.missionQueue.filter(id => { const k = missionKind(ctx, ctx.c.missionById.get(id)!); return k === 'main' || k === 'daily'; });
  if (mains.every(id => ctx.day.missions[id].done) && !ctx.day.flags.dutiesCleared) { ctx.day.flags.dutiesCleared = true; bump(ctx, 'dutyDaysCleared'); }
}

/** 每秒呼叫：旗標開始的任務、走到定點就完成的 go 步驟、逾時 */
export function tickMissions(ctx: Ctx): void {
  const d = ctx.day;
  for (const m of ctx.c.missions) {
    if (m.start.when === 'flag' && m.start.flag && (d.flags[m.start.flag] || ctx.profile.flags[m.start.flag]) && available(ctx, m)) {
      // 只有「這一天」成立的旗標才會開始 day 旗標類任務；profile 旗標（例如已經知道鑰匙在哪）也算
      startMission(ctx, m);
    }
  }
  for (const id of d.missionQueue) {
    const m = ctx.c.missionById.get(id)!; const pr = d.missions[id]; if (pr.done) continue;
    const st = m.steps[pr.step];
    if (st?.do === 'go' && st.floor === d.floor && near(ctx, st.target, 2.4)) { ctx.advance(st.minutes, m.title); finishStep(ctx, m); return; }
    const t0 = d.missionStartedAt[id];
    if (m.timeout && t0 != null && d.clock - t0 > m.timeout.minutes) {
      pr.done = true; pr.bad++;
      ctx.apply(m.timeout.effects, m.id);
      ctx.log(tr("{0}：{1}", m.title, m.timeout.text), 'bad');
      ctx.ui.card({ name: `${m.icon} ${m.title}`, role: tr("來不及了"), color: '#d9534a', badge: { text: tr("時間到"), color: '#d9534a' }, text: m.timeout.text,
        choices: [{ text: tr("下次一定要先標記"), tone: 'primary', fn: () => ctx.ui.close() }] });
      ctx.save(); return;
    }
  }
}
function near(ctx: Ctx, target: string, r: number): boolean {
  const [x, z] = ctx.world.playerPos();
  if (target.startsWith('zone:')) return ctx.world.rt().zoneAt(x, z)?.id === target.slice(5);
  const a = ctx.world.rt().anchor(target); if (!a) return false;
  return Math.max(a.box.minX - x, 0, x - a.box.maxX) ** 2 + Math.max(a.box.minZ - z, 0, z - a.box.maxZ) ** 2 <= r * r;
}

/** 洗手區洗完手：如果現在的步驟是 wash，就完成 */
export function onWashed(ctx: Ctx): void {
  for (const id of ctx.day.missionQueue) {
    const st = missionStep(ctx, id);
    if (st?.do === 'wash' && st.floor === ctx.day.floor) { finishStep(ctx, ctx.c.missionById.get(id)!); return; }
  }
}

/** 和 NPC 說話時：看看有沒有支線要開始 */
export function startByTalk(ctx: Ctx, npcId: string): Mission[] {
  const started: Mission[] = [];
  for (const m of ctx.c.missions) if (m.start.when === 'talk' && m.start.npc === npcId && available(ctx, m)) { startMission(ctx, m, true); started.push(m); }
  return started;
}

// ------------------------------------------------------------------ 執行一個步驟
/** 回傳 false 表示條件不足（例如沒推車、沒拿籃子），呼叫端要給提示 */
export function runMission(ctx: Ctx, m: Mission, extra: Partial<CardOpts> = {}): boolean {
  const st = missionStep(ctx, m.id)!; const day = ctx.day;
  const cart = day.pushing ? day.carts[day.pushing] : null;
  const kind = missionKind(ctx, m);
  const base: Omit<CardOpts, 'text' | 'choices'> = {
    name: `${m.icon} ${m.title}`, role: tr("{0}・第 {1}／{2} 步{3}", KIND_NAME[kind], day.missions[m.id].step + 1, m.steps.length, st.at ? tr("・建議 {0}", st.at) : ''),
    color: KIND_COLOR[kind], badge: { text: KIND_NAME[kind].replace(tr("任務"), ''), color: KIND_COLOR[kind] }, ...extra };
  const hintSec = { label: tr("💡 提示"), items: [missionHint(ctx, st)], tone: 'info' as const };
  const miss = missingNeeds(ctx, st);
  if (miss.length) { ctx.ui.toast(tr("🎒 背包裡要有「{0}」才能做這一步。", miss.map(id => { const it = ctx.c.packById.get(id); return `${it?.icon ?? ''} ${it?.name ?? id}`; }).join(tr("、")))); return false; }
  if (st.do === 'eat') return mealCard(ctx, () => finishStep(ctx, m, true), base);
  if (st.do === 'load' && !cart) { ctx.ui.toast(tr("先推一台烏龜車過來（靠近烏龜車按 E）。")); return false; }
  if (st.do === 'load' && cart?.load) { ctx.ui.toast(tr("這台車已經裝了一疊料，先送出去。")); return false; }
  if ((st.do === 'deliver' || (st.do === 'freight' && st.needsCart !== false)) && !cart?.load) { ctx.ui.toast(tr("要推著裝好料的烏龜車過來。")); return false; }
  if (st.do === 'carry') {
    const bid = st.carry && basketHeldOrOnCart(ctx, st.carry.content);
    if (!bid) { const ing = basketLabel(ctx.c, st.carry!.content); ctx.ui.toast(tr("🧺 要先拿一籃「{0}」過來（拿在手上或放在推著的烏龜車上）。", ing)); return false; }
    ctx.advance(st.minutes, m.title); consumeBasket(ctx, bid, st.target);
    ctx.ui.card({ ...base, text: tr("{0}\n放好了！", st.say), badge: { text: tr("✔ 送到了"), color: '#3f9e6c' }, choices: [{ text: tr("好"), tone: 'primary', fn: () => { ctx.ui.close(); finishStep(ctx, m, true); } }] });
    bump(ctx, 'basketsDelivered');
    return true;
  }

  const apply = (opt?: NonNullable<MissionStep['choose']>['options'][number]) => {
    ctx.advance(st.minutes, m.title);
    if (opt?.effects) ctx.apply(opt.effects, m.id);
    if (opt?.flag) day.flags[opt.flag] = true;
    if (st.do === 'load' && cart && st.load) {
      const baskets = opt?.baskets ?? 4;
      cart.load = { material: st.load.material, baskets, kg: st.load.kgPerBasket };
      if (baskets <= ctx.c.game.carry.warnAbove) bump(ctx, 'safeStack');
      if (st.load.kgPerBasket > ctx.c.game.carry.heavyKg && opt?.good) bump(ctx, 'heavySafe');
    }
    if (st.do === 'deliver' && cart) { cart.load = null; ctx.log(tr("送達：{0}", st.say), 'good'); }
    if (st.do === 'freight' && cart) {
      const to = st.toFloor!; const fl = ctx.c.floorById.get(to)!;
      const here = ctx.c.floorById.get(st.floor)!.equipment.find(e => e.id === st.target);
      const grp = here?.interaction?.type === 'freight' ? here.interaction.group : undefined;
      const fr = fl.equipment.find(e => e.interaction?.type === 'freight' && (!grp || e.interaction.group === grp)) ?? fl.equipment.find(e => e.interaction?.type === 'freight')!;
      cart.floor = to; cart.pos = [fr.pos[0] + 0.2, fr.pos[1] + fr.size[2] / 2 + 1.1];
      for (const b of cart.baskets) { const bs = day.baskets[b]; if (bs) bs.floor = to; }
      day.pushing = null;
      ctx.ui.toast(tr("🛗 物料已送上 {0}。人走樓梯。", to));
    }
    if (st.do === 'retrieve') {
      const here = Object.entries(day.carts).find(([, c]) => c.floor === day.floor && c.load);
      if (here) day.pushing = here[0];
    }
    if (opt?.injuryChance && nextRandom(day) < opt.injuryChance) {
      day.injuries.push({ label: opt.injury ?? tr("搬運受傷"), severity: 'light', at: day.clock, source: m.id });
      day.player.hp = Math.max(0, day.player.hp - 20);
      ctx.ui.toast(tr("🩹 {0}——下午整個人只能用走的。", opt.injury ?? tr("受傷")));
    }
  };

  if (st.do === 'minigame' && st.game) {
    const params: Record<string, unknown> = { ...(st.params ?? {}) };
    if (params.useTemps) params.spots = tempSpots(ctx, String(params.useTemps === true ? st.floor : params.useTemps)).map(({ id, name, kind, limit, value, read }) => ({ id, name, kind, limit, value, read }));
    ctx.ui.work(st.game, params, r => miniDone(ctx, m, st, r), m.title);
    return true;
  }
  if (st.do === 'training') { ctx.ui.training(() => { ctx.advance(st.minutes, m.title); bump(ctx, 'trained'); gainXp(ctx, 'training', m.title); ctx.log(tr("🏆 新人教育訓練結業"), 'good'); finishStep(ctx, m, true); }); return true; }
  if (st.do === 'count' && st.count) return countCard(ctx, m, st, base, hintSec);
  if (st.do === 'calc' && st.calc) return calcCard(ctx, m, st, base, hintSec, 0, 0);

  if (st.choose) {
    const order = shuffle(st.choose.options.map((_, i) => i), { rng: m.id.length * 131 + day.missions[m.id].step + day.day });
    ctx.ui.card({ ...base, title: st.say, text: st.choose.ask,
      choices: order.map(i => {
        const o = st.choose!.options[i];
        return { text: `${o.icon ? o.icon + ' ' : ''}${o.text}`, fn: () => {
          apply(o);
          ctx.ui.card({ ...base, text: o.result, badge: { text: o.good ? tr("✔ 好判斷") : tr("✘ 可以更好"), color: o.good ? '#3f9e6c' : '#d9534a' }, expression: o.good ? 'happy' : 'worried',
            choices: [{ text: tr("繼續"), tone: 'primary', fn: () => { ctx.ui.close(); finishStep(ctx, m, o.good); } }] });
        } };
      }) });
  } else {
    ctx.ui.card({ ...base, text: st.say, choices: [{ text: actionVerb(st), tone: 'primary', fn: () => { apply(); ctx.ui.close(); finishStep(ctx, m); } }] });
  }
  return true;
}

/** v1.0 作業視窗關掉之後 */
function miniDone(ctx: Ctx, m: Mission, st: MissionStep, r: MiniResult | null): void {
  if (!r) { ctx.ui.toast(tr("作業還沒做完，這一步先保留（可以再來一次）。")); return; }
  bump(ctx, 'minigamesPlayed');
  if (r.fine && r.fine > 0) { ctx.day.factory.fines += r.fine; ctx.log(tr("罰款 ${0}：{1}", r.fine, m.title), 'bad'); bus.emit('stateChanged', undefined); }
  if (r.ok) {
    ctx.advance(st.minutes, m.title);
    bump(ctx, 'minigamesDone'); if (r.score >= 0.95) bump(ctx, 'minigamesPerfect');
    gainXp(ctx, 'minigame', m.title);
    if (r.achievement) { ctx.log(`🏆 ${r.achievement}`, 'good'); setTimeout(() => ctx.ui.toast(`🏆 ${r.achievement}`), 900); }
    finishStep(ctx, m, r.score >= 0.8);
    return;
  }
  ctx.advance(Math.max(1, Math.round(st.minutes / 2)), m.title);
  if (r.scold) scold(ctx, r.scold, st.floor);
  if (r.failMission) {
    const pr = ctx.day.missions[m.id]; pr.done = true; pr.failed = true; pr.bad++;
    ctx.log(tr("任務失敗：{0}——{1}", m.title, r.summary), 'bad');
    if (canRetry(ctx, m.id)) setTimeout(() => ctx.ui.toast(tr("🔁 失敗的任務今天可以再挑戰一次：任務欄點「{0}」→「再挑戰一次」，或回到機台按 E。", m.title)), 1200);
    ctx.world.refreshActors(); ctx.save();
  } else if (!r.scold) ctx.ui.toast(tr("沒有完成：{0}（可以再做一次）", r.summary));
}
/** v1.0 被主管罵：這層樓的主管（沒有就是值班主管） */
export function scold(ctx: Ctx, text: string, floor?: string, then?: () => void): void {
  const boss = ctx.c.npcs.find(n => n.floor === (floor ?? ctx.day.floor) && /blue[123]|chief/.test(n.hat)) ?? null;
  ctx.apply({ trust: -4 }, 'scold'); ctx.day.player.stress = Math.min(100, ctx.day.player.stress + 10);
  bump(ctx, 'scolded'); bus.emit('sfx', 'warn');
  ctx.log(tr("被{0}罵了：{1}", boss?.name ?? tr("值班主管"), text), 'bad');
  ctx.ui.card({ name: boss?.name ?? tr("值班主管"), role: boss?.title ?? tr("生產部"), color: boss?.color ?? '#c98f6b', npc: boss?.id, expression: 'angry', badge: { text: tr("😡 被罵了"), color: '#d9534a' },
    text, choices: [{ text: tr("對不起，我下次會注意"), tone: 'primary', fn: () => { ctx.ui.close(); then?.(); } }] });
}

const EMOJI: Record<string, string> = { 醬油: '🍶', 砂糖: '🧂', 米酒: '🍶', 香精: '🧪', 色素: '🧪' };
/** 盤點：把東西畫成一排圖示，數完選數字（三選一） */
function countCard(ctx: Ctx, m: Mission, st: MissionStep, base: Omit<CardOpts, 'text' | 'choices'>, hint: { label: string; items: string[]; tone: 'info' }): boolean {
  const c = st.count!; const icon = EMOJI[c.item] ?? '📦';
  const opts = shuffle([c.answer, c.answer + 1, Math.max(1, c.answer - 2)].filter((v, i, a) => a.indexOf(v) === i), { rng: m.id.length + ctx.day.day });
  let tries = 0;
  const ask = () => ctx.ui.card({ ...base, title: st.say, text: tr("架上的「{0}」：\n\n{1}\n\n一共有幾{2}？", c.item, icon.repeat(c.answer), c.unit), sections: tries ? [hint] : undefined,
    choices: opts.map(n => ({ text: `${n} ${c.unit}`, fn: () => {
      if (n === c.answer) {
        ctx.advance(st.minutes, m.title); ctx.apply({ records: tries ? 1 : 2 }, m.id);
        ctx.ui.card({ ...base, text: tr("對！{0} 一共 {1} {2}。已經記到盤點表上。", c.item, n, c.unit), badge: { text: tr("✔ 數對了"), color: '#3f9e6c' }, expression: 'happy',
          choices: [{ text: tr("繼續"), tone: 'primary', fn: () => { ctx.ui.close(); finishStep(ctx, m, tries === 0); } }] });
      } else { tries++; ctx.ui.toast(tr("再數一次看看！一個一個點著數。")); ask(); }
    } })) });
  ask();
  return true;
}

/** 配料計算：一種材料一張卡，三選一；選錯可以再算一次（看提示） */
function calcCard(ctx: Ctx, m: Mission, st: MissionStep, base: Omit<CardOpts, 'text' | 'choices'>, hint: { label: string; items: string[]; tone: 'info' }, i: number, wrong: number): boolean {
  const c = st.calc!; const item = c.recipe[i];
  const ans = round(item.grams * c.packs);
  const opts = shuffle([...new Set([ans, item.grams, round(item.grams * c.packs * 10), round(ans + item.grams * 0.1) || ans + 1])].slice(0, 3), { rng: i * 17 + m.id.length });
  if (!opts.includes(ans)) opts[0] = ans;
  const table = c.recipe.map((r, j) => tr("{0} {1}：1 包 {2} g{3}", j < i ? '✔' : j === i ? '▶' : tr("・"), r.name, r.grams, j < i ? ` → ${round(r.grams * c.packs)} g` : ''));
  ctx.ui.card({ ...base, title: tr("{0} {1} 包", c.product, c.packs), text: tr("{0}：1 包要 {1} g。\n今天只要 {2} 包，要秤幾克？", item.name, item.grams, c.packs),
    sections: [{ label: tr("配方（1 包）"), items: table }, ...(wrong ? [hint, { label: tr("算法"), items: [`${item.grams} × ${c.packs} = ?`], tone: 'warn' as const }] : [])],
    choices: opts.map(v => ({ text: `${v} g`, fn: () => {
      if (v !== ans) { ctx.ui.toast(tr("不對喔！{0} × {1} 再算一次。", item.grams, c.packs)); bump(ctx, 'calcRetry'); calcCard(ctx, m, st, base, hint, i, wrong + 1); return; }
      if (i + 1 < c.recipe.length) { calcCard(ctx, m, st, base, hint, i + 1, wrong); return; }
      ctx.advance(st.minutes, m.title); ctx.apply({ records: wrong ? 1 : 3 }, m.id); bump(ctx, 'calcDone');
      ctx.ui.card({ ...base, text: tr("秤好了：{0}。綁成一包。", c.recipe.map(r => `${r.name} ${round(r.grams * c.packs)} g`).join(tr("、"))), badge: { text: tr("✔ 算對了"), color: '#3f9e6c' }, expression: 'happy',
        choices: [{ text: tr("繼續"), tone: 'primary', fn: () => { ctx.ui.close(); finishStep(ctx, m, wrong === 0); } }] });
    } } as CardChoice)) });
  return true;
}
const round = (v: number) => Math.round(v * 10) / 10;

const actionVerb = (st: MissionStep) => st.need && !st.needs ? tr("交出「{0}」", st.need) : ({ minigame: tr("開始作業"), training: tr("上課"), use: tr("完成"), talk: tr("好，知道了"), go: tr("到了"), load: tr("裝上車"), deliver: tr("交料、請對方簽收"), freight: tr("送進貨梯"), retrieve: tr("把車推出來"), carry: tr("放下"), wash: tr("洗好了"), count: tr("數好了"), calc: tr("算好了"), label: tr("貼好了"), eat: tr("吃飽了") } as Record<string, string>)[st.do] ?? tr("完成");

/** 3F 員工餐廳：吃飯（一天一次有效果；任務的「吃飯」步驟也走這裡） */
export function mealCard(ctx: Ctx, then?: () => void, base?: Partial<CardOpts>): boolean {
  const d = ctx.day;
  const P = ctx.c.game.player;
  if (d.flags.ateLunch && !then) { ctx.ui.toast(tr("今天已經吃過了。下一梯的同事在等位子，讓給他們吧。")); return true; }
  const eat = () => {
    ctx.advance(30, tr("用餐"));
    if (!d.flags.ateLunch) { d.player.fatigue = Math.max(0, d.player.fatigue - P.restFatigueRecover); d.player.hydration = 100; d.player.stress = Math.max(0, d.player.stress - 8); d.player.focus = Math.min(100, d.player.focus + 10); }
    if (d.flags.extraDish) { d.player.stress = Math.max(0, d.player.stress - 15); d.player.focus = Math.min(100, d.player.focus + 15); d.flags.extraDish = false; ctx.ui.toast(tr("🍗 加菜！心情更好了。")); }
    if (!d.flags.ateLunch) gainXp(ctx, 'meal');
    d.flags.ateLunch = true; bump(ctx, 'meals'); bus.emit('sfx', 'ok');
    ctx.log(tr("吃午餐（員工餐廳）"), 'good'); ctx.ui.close(); ctx.ui.toast(tr("🍱 吃飽了！體力回來了一點。")); then?.(); ctx.save();
  };
  ctx.ui.card({ name: tr("🍱 員工餐廳"), role: tr("3F・4 張長桌、椅子自己拿"), color: '#e0a73a', ...base,
    text: tr("員工餐由 2F 調理區準備。餐廳只坐得下約 30 人，所以廠務把全廠分成好幾梯（10:30 起每半小時一梯）。\n吃飯前，手上的工作要先交給同事；吃完回生產區要重新換裝、過氣浴室、洗手。"),
    sections: [{ label: tr("吃飯 30 分鐘"), items: [tr("疲勞 −{0}", P.restFatigueRecover), tr("水分補滿"), tr("壓力 −8、專注 +10")], tone: 'good' }],
    choices: [{ text: tr("🍱 打菜、坐下來吃"), tone: 'primary', fn: eat }, { text: tr("等一下再吃"), fn: () => ctx.ui.close() }] });
  return true;
}

/** v1.0 自由模式（實習生）：可以待的區域＝非生產區＋自己職務的樓層 */
export function internAllowed(ctx: Ctx, floor: string, level: number): boolean {
  const role = ctx.c.roleById.get(ctx.day.roleId)!;
  return level < 0 || floor === role.startFloor;
}
/** v1.0 實習生跑出自己的區域：附近有主管（幹部帽、課長、巡場）就會被罵爛 */
export function supervisorNear(ctx: Ctx, x: number, z: number, r = 6): Npc | null {
  const w = whoSees(ctx, x, z); if (w) return w;
  for (const n of ctx.c.npcs) if (n.floor === ctx.day.floor && n.pos && /blue[123]|chief/.test(n.hat) && Math.hypot(n.pos[0] - x, n.pos[1] - z) <= r) return n;
  return null;
}
